import React, { useState } from 'react';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { CalendarEvent, CalendarViewType } from '@/types/calendar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { format, addDays, startOfDay, endOfDay, addHours } from 'date-fns';
import { ChevronLeft, ChevronRight, Plus, Trash } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import EventItem from './EventItem';

interface CalendarViewProps {
  events: CalendarEvent[];
  viewType: CalendarViewType;
  currentDate: Date;
  onViewChange: (viewType: CalendarViewType) => void;
  onDateChange: (date: Date) => void;
  onEventAdd: (event: CalendarEvent) => void;
  onEventUpdate: (event: CalendarEvent) => void;
  onEventDelete: (eventId: string) => void;
}

const CalendarView: React.FC<CalendarViewProps> = ({
  events,
  viewType,
  currentDate,
  onViewChange,
  onDateChange,
  onEventAdd,
  onEventUpdate,
  onEventDelete
}) => {
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [newEvent, setNewEvent] = useState<Partial<CalendarEvent>>({
    title: '',
    description: '',
    start: new Date(),
    end: addHours(new Date(), 1),
    location: '',
    calendarId: 'local',
    source: 'local'
  });
  
  const handleAddEvent = () => {
    if (newEvent.title && newEvent.start && newEvent.end) {
      onEventAdd({
        ...newEvent as CalendarEvent,
        id: `event-${Date.now()}`,
      });
      setShowEventDialog(false);
      setNewEvent({
        title: '',
        description: '',
        start: new Date(),
        end: addHours(new Date(), 1),
        location: '',
        calendarId: 'local',
        source: 'local'
      });
    }
  };
  
  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      setSelectedDate(date);
      setNewEvent(prev => ({
        ...prev,
        start: startOfDay(date),
        end: endOfDay(date)
      }));
      setShowEventDialog(true);
    }
  };
  
  const handlePrevious = () => {
    if (viewType === 'month') {
      const prevMonth = new Date(currentDate);
      prevMonth.setMonth(prevMonth.getMonth() - 1);
      onDateChange(prevMonth);
    } else if (viewType === 'week') {
      onDateChange(addDays(currentDate, -7));
    } else if (viewType === 'day') {
      onDateChange(addDays(currentDate, -1));
    }
  };
  
  const handleNext = () => {
    if (viewType === 'month') {
      const nextMonth = new Date(currentDate);
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      onDateChange(nextMonth);
    } else if (viewType === 'week') {
      onDateChange(addDays(currentDate, 7));
    } else if (viewType === 'day') {
      onDateChange(addDays(currentDate, 1));
    }
  };
  
  const handleToday = () => {
    onDateChange(new Date());
  };
  
  const getDayEvents = (date: Date) => {
    return events.filter(event => {
      const eventDate = new Date(event.start);
      return (
        eventDate.getDate() === date.getDate() &&
        eventDate.getMonth() === date.getMonth() &&
        eventDate.getFullYear() === date.getFullYear()
      );
    });
  };
  
  const renderMonthView = () => {
    return (
      <Calendar
        mode="single"
        selected={selectedDate}
        onSelect={handleDateSelect}
        month={currentDate}
        onMonthChange={onDateChange}
        className="rounded-md border w-full"
      />
    );
  };
  
  const renderDayView = () => {
    const dayEvents = getDayEvents(currentDate);
    
    return (
      <div className="space-y-2">
        <div className="text-lg font-semibold text-center py-2">
          {format(currentDate, 'EEEE, MMMM d, yyyy')}
        </div>
        <ScrollArea className="h-[500px] pr-4">
          {dayEvents.length > 0 ? (
            <div className="space-y-2">
              {dayEvents.map(event => (
                <EventItem 
                  key={event.id} 
                  event={event} 
                  onUpdate={onEventUpdate} 
                  onDelete={onEventDelete}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              No events scheduled for this day
            </div>
          )}
        </ScrollArea>
      </div>
    );
  };
  
  const renderAgendaView = () => {
    const futureEvents = events
      .filter(event => new Date(event.start) >= startOfDay(new Date()))
      .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
      
    return (
      <ScrollArea className="h-[500px] pr-4">
        {futureEvents.length > 0 ? (
          <div className="space-y-4">
            {futureEvents.map(event => (
              <EventItem 
                key={event.id} 
                event={event} 
                onUpdate={onEventUpdate} 
                onDelete={onEventDelete}
                showDate
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-muted-foreground">
            No upcoming events scheduled
          </div>
        )}
      </ScrollArea>
    );
  };
  
  return (
    <div className="p-4 space-y-4">
      <div className="flex justify-between items-center">
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrevious}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleNext}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleToday}
          >
            Today
          </Button>
          <div className="text-lg font-semibold ml-2">
            {format(currentDate, 'MMMM yyyy')}
          </div>
        </div>
        
        <div className="flex gap-2">
          <Tabs defaultValue={viewType} onValueChange={(v) => onViewChange(v as CalendarViewType)}>
            <TabsList>
              <TabsTrigger value="month">Month</TabsTrigger>
              <TabsTrigger value="day">Day</TabsTrigger>
              <TabsTrigger value="agenda">Agenda</TabsTrigger>
            </TabsList>
          </Tabs>
          
          <Button
            size="sm"
            onClick={() => {
              setNewEvent({
                title: '',
                description: '',
                start: new Date(),
                end: addHours(new Date(), 1),
                location: '',
                calendarId: 'local',
                source: 'local'
              });
              setShowEventDialog(true);
            }}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Event
          </Button>
        </div>
      </div>

      {viewType === 'month' && renderMonthView()}
      {viewType === 'day' && renderDayView()}
      {viewType === 'agenda' && renderAgendaView()}
      
      <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add New Event</DialogTitle>
            <DialogDescription>
              Create a new event in your calendar
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Event Title</Label>
              <Input
                id="title"
                placeholder="Enter event title"
                value={newEvent.title || ''}
                onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date & Time</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start">
                      {newEvent.start ? format(newEvent.start, 'PPp') : 'Select date & time'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={newEvent.start}
                      onSelect={(date) => date && setNewEvent({ ...newEvent, start: date })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              <div className="space-y-2">
                <Label>End Date & Time</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start">
                      {newEvent.end ? format(newEvent.end, 'PPp') : 'Select date & time'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={newEvent.end}
                      onSelect={(date) => date && setNewEvent({ ...newEvent, end: date })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                placeholder="Enter location (optional)"
                value={newEvent.location || ''}
                onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Enter event details (optional)"
                value={newEvent.description || ''}
                onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEventDialog(false)}>Cancel</Button>
            <Button onClick={handleAddEvent} disabled={!newEvent.title}>Add Event</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CalendarView;
